import React from 'react';
import styled, { FlattenInterpolation } from 'styled-components';
import { color } from 'styled-system';
import { EnergyEfficiencyGrade, Product, ProductFlags } from '@packages/common';
import { Translate } from '@packages/i18n';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { themeGet, media, ThemeColors, is } from '@packages/ui';

import { formattedPrice } from '../../../utils';
import { EnergyRating } from './EnergyRating';

const APP_TLD = getPublicRuntimeConfig('APP_TLD');

export interface PromotionFlagProps {
  voucher: Product['voucher'];
}

export interface PromotionFlagProps {
  voucher: Product['voucher'];
  flags?: Product['flags'];
  hidePromotionsOnXS: boolean;
  customCss?: FlattenInterpolation<any>;
  energyEfficency?: EnergyEfficiencyGrade;
}

const Strong = styled.span`
  font-weight: ${themeGet.fontWeight('bold')};
`;

const Line = styled.div``;

const EnergyEfficiencyWrapper = styled.div`
  position: absolute;
  left: 0; /* required for iOS 9.3 */
  bottom: -9px;
  display: flex;
`;

const Wrapper = styled.div<{
  bg: ThemeColors;
  color?: ThemeColors;
  hidePromotionsOnXS: boolean;
  customCss?: PromotionFlagProps['customCss'];
}>`
  position: absolute;
  left: 0; /* required for iOS 9.3 */
  background-color: ${({ bg }) => themeGet.colors(bg)};
  ${color}
  box-shadow: 0 0 3px 2px rgba(43, 18, 2, 0.07);
  padding: ${themeGet.space(1)} ${themeGet.space(2)};
  border-radius: 0 6px 6px 0;
  height: 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 85%;
  bottom: -8px;
  div {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
  }

  ${media.to.sm`
    max-width: 98%;
    ${is('hidePromotionsOnXS')`
      display: none;
    `}
  `};

  ${media.from.md`
    font-size: ${themeGet.fontSize(4)};
  `};

  ${(props) => props.customCss}
`;

Wrapper.defaultProps = {
  color: 'sandDune',
};

export const PromotionFlags: React.FC<PromotionFlagProps> = ({
  voucher,
  flags,
  hidePromotionsOnXS,
  customCss,
  energyEfficency,
}) => {
  /*
   * CAP-305
   * the energy label will be introduced as a cascade on the same position as the voucher flag.
   * the energy level always beats the voucher when both options are given
   */
  if (energyEfficency) {
    return (
      <EnergyEfficiencyWrapper data-testid="energy-label">
        <EnergyRating energyEfficency={energyEfficency} />
      </EnergyEfficiencyWrapper>
    );
  }

  if (!flags) {
    return null;
  }

  if (flags.includes(ProductFlags.promotion) && voucher) {
    const { code, text, valueAmount, valuePercentage } = voucher;

    return (
      <Wrapper bg="white" hidePromotionsOnXS={hidePromotionsOnXS} customCss={customCss}>
        <Line>
          <Strong>
            {!!valueAmount && formattedPrice(valueAmount, APP_TLD)}
            {!valueAmount && !!valuePercentage && `${valuePercentage.toFixed(0)}%`}
          </Strong>{' '}
          {text}
        </Line>
        <Line>
          {code && (
            <>
              <Translate translation="code" />:<Strong>{code}</Strong>
            </>
          )}
        </Line>
      </Wrapper>
    );
  }

  if (flags.includes(ProductFlags.topseller)) {
    return (
      <Wrapper bg="bermudas" color="white" hidePromotionsOnXS={hidePromotionsOnXS} customCss={customCss}>
        <Translate translation="topseller" />
      </Wrapper>
    );
  }

  if (flags.includes(ProductFlags.favorite)) {
    return (
      <Wrapper bg="bermudas" color="white" hidePromotionsOnXS={hidePromotionsOnXS} customCss={customCss}>
        <Translate translation="favoriteFlag" />
      </Wrapper>
    );
  }

  if (flags.includes(ProductFlags.recommended)) {
    return (
      <Wrapper bg="bermudas" color="white" hidePromotionsOnXS={hidePromotionsOnXS} customCss={customCss}>
        <Translate translation="recommended" />
      </Wrapper>
    );
  }

  return null;
};
